import Backbone from 'backbone'
import template from './filter-step1.hbs'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import arrayToHash from 'scoreboard/components/array-to-hash'
import DatasetsNumericFilter from './datasets-numeric-filter'
import DatasetsTextFilter from './datasets-text-filter'
import DatasetsYesNoFilter from './datasets-yes-no-filter'
import DatasetsRollupFilterStep1 from './datasets-rollup-filter-step1'
import DatasetsDateFilterStep1 from './datasets-date-filter-step1'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import twoSteps from './two-steps'
import deepEqual from 'deep-equal'
import renderFieldDropdown from '../render-field-dropdown'
import unwrapModel from 'scoreboard/components/unwrap-model'
import selects from 'scoreboard/components/selects'
import * as d3 from 'd3-selection'
import DatasetsFilterEquationForm from './datasets-filter-equation-form'
import appDisplay from 'scoreboard/components/app-display'
import renderValuesSelect from './render-values-select'
import trimArray from 'scoreboard/components/trim-array'
import Sorter from 'scoreboard/components/sorter'
import nextLinkGroup from './next-link-group'
import PathToDatasetButton from 'scoreboard/datasets/edit/path-to-dataset-button'
import deleteNonApplicableFilterProperties from './delete-non-applicable-filter-properties'
import momentHelper from 'scoreboard/components/moment-helper'
import datasetLabel from 'scoreboard/forms/edit/dataset-label.hbs'
import { filterNumericValues, getAllValuesFromSuggestionsForFilterValue } from 'scoreboard/datasets/filters/filter-numeric-values'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import beginsEndsWithOperators from 'scoreboard/datasets/filters/begins-ends-with-operators'
import Dataset, { isEditField } from '../model/dataset'
const { DATASET } = Dataset
import renderStrongOperatorDropdown from './render-strong-operator-dropdown'
import { convertPercentValuesForValuesSelect } from 'scoreboard/imports/transform/value-converter-for-values-select'
import moveDecimal from 'scoreboard/components/move-decimal'
import DatasetsFilterBadges from './datasets-filter-badges'
import DatasetButtonControls from '../dataset-button-controls'
import wrapModel from '../../components/wrap-model'
import DataType from 'scoreboard/datasets/model/data-type'
const {
    TEXT,
    YES_NO,
    DATE,
    DATE_TIME,
    TIME,
    CALENDAR_PERIOD,
    CLUSTER,
    ROLLUP,
    PERCENT,
} = DataType

/**
 * Returns true if the object is null, undefined, an empty string, or an
 * array which only contains null, undefined, or empty string elements
 */
function isEmpty(object) {
	if (_.isEmpty(object)) return true

	let nonEmptyElement = _.find(object, element => !_.isEmpty(element))
	return !nonEmptyElement
}

var FilterStep1 = Backbone.View.extend({

	className: 'filter-step',

	tagName: 'form',

	events: {
		'change [name=datasetId]': 'changeDataset',
		'change [name=fieldIdentifier]': 'changeField',
		'change [name=operator]': 'changeOperator',
		'change [name=values]': 'bindSelectItems',
		'click .add-equation': 'editEquation',
		'click .badge': 'clickBadge',
		'click .filter-delete': 'deleteFilter'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the Dataset
	 *
	 * @param {Object} options.filter the filter we're working with
	 *
	 * @param {Boolean} [options.lockField=false] indicates the field dropdown
	 * should be disabled so that its value cannot be changed
	 *
	 * @param {Array} options.filters the filters already applied to the dataset
	 *
	 * @param {Boolean} options.showPermissionFilters whether we should show permission filters
	 *
	 * @param {Boolean} [options.showAggregationFilters=false] whether we should
	 * show filters that apply to aggregations (aka columns)
	 *
	 * @param {String} [options.title] optional title for the dialog
	 *
	 * @param {Boolean} options.showDelete whether we should show the delete button
	 *
	 * @param {Boolean} [options.allLinkedFields=false] whether to show all
	 * linked fields rather than just the explicitly defined visible linked
	 * fields
	 *
	 * @param {Boolean} [options.showDatasetLink=false] if a link to the dataset
	 * should be displayed
	 *
	 * @param {Boolean} [options.showDatasetButton=false] whether there should
	 * be a button that allows selection of an arbitrary dataset
	 *
	 * @param {Dataset[]} options.datasetsById
	 *
	 * @param {Boolean} [options.includeEditFields=true] whether to include the
	 * "Record is Editable" and "Record Edited" fields in the dropdown (#54607)
	 */
	initialize: function(options = {}) {
		this.options = options
		this.options.filter = _.cloneDeep(this.options.filter)
		this.lockField = _.get(options, 'lockField', false)
		this.showDatasetButton = options.showDatasetButton ?? false
		this.showAggregationFilters = _.isNil(options.showAggregationFilters) ?
			false : options.showAggregationFilters
		var FilterStep1FormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new FilterStep1FormView({
			el: this.el,
			guard: false,
			submit: this.submit.bind(this),
			allowCleanSubmission: !!this.options.filter?.fieldId
		})
		this.filters = _.filter(this.options.filters, (filter) =>
			!_.isNil(filter.operator))
		if (!options.showPermissionFilters) {
			this.filters = _.filter(this.filters, f => _.isNil(f.permissionId))
		}
		if (!this.showAggregationFilters) {
			this.filters = _.filter(this.filters, f => _.isNil(f.aggregationId))
		}

		let equations = this.options.filter?.equations ?? []
		// any equation values that are in the select specified by
		// this.equationSelector()
		this.equations = _.clone(equations)
		// all equation values that have ever been in the select specified by
		// this.equationSelector(). Allows remembering equations that were
		// previously selected so that they can be re-added as equations instead
		// of as values
		this.allEquations = _.clone(equations)
		this.allLinkedFields = _.get(this.options, 'allLinkedFields', false)
		this.includeEditFields = options.includeEditFields ?? true
	},

	render: function() {
		var title = this.options.title ||
			i18n.get(this.options.lockField ? 'common.edit2' : 'common.add2',
			'datasets.filter')

		this.$el.empty().append(template({ title }))

		this.renderDatasetLabel()
		this.renderDatasetSelect()
		this.renderDatasetButtonControls()
		this.renderPathToDatasetButton()
		this.togglePathToDatasetButton()
		this.renderFieldSelect()
		this.maybeShowZoneAdjustmentBanner()
		this.operatorField = new MarkedField({
			el: this.$('[name=operator]'),
			validator: requiredValidator
		}).render()

		this.options.wizard?.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})
		// this will also trigger this.changeOperator if a field has been selected
		this.changeField()

		// "Add to Existing Filter As An OR" should be checked if there is
		// another filter with the same linkGroup as the one we're editing
		this.inLinkGroup = _.find(this.filters, (f) =>
			!deepEqual(f, this.options.filter) &&
			f.linkGroup == this.options.filter.linkGroup &&
			!_.isNil(f.linkGroup))

		let filters = this.renderChooseFilterToggle()
		let noneMessage = i18n.get('common.thereAreNo', 'datasets.records.activeFilters')
		if (_.size(filters) != _.size(this.filters)) {
			noneMessage = i18n.get('datasets.filters.noOthers')
		}

		this.filterBadges = new DatasetsFilterBadges({
			model: this.model,
			filters: filters,
			treeItemsById: this.options.treeItemsById,
			isEditable: false,
			noneMessage: noneMessage,
			showPermissionFilters: this.options.showPermissionFilters,
			datasetsById: this.options.datasetsById
		})
		this.$('.choose-filter .panel-content').html(this.filterBadges.el)
		this.filterBadges.render()
		_.each(this.$('.filter-badges .filter'), (el, index) => {
			if (this.filterBadges.filteredFilters[index].linkGroup == this.options.filter.linkGroup &&
				!_.isNil(this.options.filter.linkGroup)) {
				this.$(el).closest('.badge').addClass('is-selected')
			}
		})
		// #13991 if there's only one filter, select it by default
		if (this.$('.badge').length == 1) {
			this.$('.badge').addClass('is-selected')
		}

		this.$('.filter-delete').toggleClass('is-not-displayed', !this.options.showDelete)

		this.renderMatchingToggle()

		this.equationSelect = this.$(this.equationSelector())
		this.equationSelect.change(this.onChangeEquationSelect.bind(this))

		this.formView.start()

		// If a dataset was already selected, don't focus on the dataset select on render #33331
		// Instead, focus the second field selector
		if (this.$('[name=datasetId').val() != null) {
			selects.toggleFocus(this.$('[name=datasetId'), false)
			selects.toggleFocus(this.$('[name=fieldIdentifier]'), true)
		}
		return this
	},

	renderMatchingToggle: function(field) {
		this.matchingToggle && this.matchingToggle.remove()

		const showToggle = this.isDisplayingMatchingToggle(field)
		this.$('.matching-toggle')
			.toggleClass('is-not-displayed', !showToggle)
		// #18479 handle "Matching" field
		if (!showToggle) return

		const anyLabel = i18n.get('datasets.filters.matching')
		const noneLabel = i18n.get('datasets.filters.notMatching')
		const matching = _.get(this.options.filter, 'matching', 'any')
		this.matchingToggle = new LabeledToggle({
			name: 'matching',
			options: [{
				value: 'any',
				label: anyLabel
			}, {
				value: 'none',
				label: noneLabel
			}],
			value: matching,
			escapeLabels: false
		})
		this.$('.matching-toggle').append(this.matchingToggle.el)
		this.matchingToggle.render()
		this.matchingToggle.on('change', () => this.formView.change())
	},

	/** Returns whether the Matching/Not Matching toggle should display */
	isDisplayingMatchingToggle: function(field) {
		if (_.isNil(field)) {
			field = this.field()
		}


		const fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		if (_.isNil(fieldIdentifier)) return false

		if (isEditField(fieldIdentifier)) {
			return false
		}

		if (_.isNil(field)) return false
		const vlf = this.model?.getVisibleLinkedFieldByFieldIdentifier(fieldIdentifier)

		let datasetId = _.isNil(vlf) ? field.datasetId : vlf.datasetId
		return datasetId != this.model?.get('rootDatasetId')
	},

	renderChooseFilterToggle: function() {
		this.chooseFilterToggle = new OnOffToggle({
			name: 'chooseFilterToggle',
			checked: this.inLinkGroup,
			label: i18n.get('datasets.orGroup.toggle')
		})
		this.chooseFilterToggle.on('change', function() {
			this.formView.change()
			this.$('.choose-filter').toggleClass('is-not-displayed',
				!this.chooseFilterToggle.val())
		}.bind(this))
		this.$('.choose-filter-toggle').append(this.chooseFilterToggle.el)
		this.chooseFilterToggle.render()
		// AFTER the toggle is in the DOM
		this.chooseFilterToggle.trigger('change')

		let filters = this.filters
		// if the filter being edited isn't in an existing or group, exclude it
		// from the filters displayed by filter-badges
		if (!this.inLinkGroup) {
			var filterWithoutLinkGroup = _.omit(this.options.filter, 'linkGroup')
			this.filters = _.filter(this.filters, (f) => {
				var fWithoutLinkGroup = _.omit(f, 'linkGroup')
				return !deepEqual(fWithoutLinkGroup, filterWithoutLinkGroup)
			})
		}
		// #13989 hide OR filter toggle when no other active filters
		if (_.size(filters) == 0) {
			this.$('.choose-filter-toggle').addClass('is-not-displayed')
		}

		return filters
	},

	maybeShowZoneAdjustmentBanner: function() {
		const browserZone = momentHelper.defaultTimeZone()
		const field = this._getField()
		const fieldZone = _.result(field, 'timeZone') || browserZone
		const operator = this.$('[name=operator]').val()
		const isTimeFilter = operator === 'at-any-time'

		const bannerEl = this.$('.banner.banner-description')
		if (browserZone !== fieldZone && isTimeFilter) {
			const msg = i18n.get('datasets.filters.time.adjustmentExplanation',
				browserZone, fieldZone)
			bannerEl.html(msg).removeClass('is-not-displayed')
		} else {
			bannerEl.addClass('is-not-displayed')
		}
	},

	renderDatasetLabel: function() {
		if (this.showDatasetButton) {
			this.$('.dataset-label-container').addClass('is-not-displayed')
		}

		if (this.options.showDatasetLink) {
			this.$('.dataset-label-container').append(datasetLabel({
				label: _.get(this.model?.attributes, 'label'),
				href: `datasets/${_.get(this.model?.attributes, 'id')}`
			}))
		}
	},

	renderDatasetSelect: function() {
		if (this.showDatasetButton) {
			this.$('.dataset-id').addClass('is-not-displayed')
			return
		}

		if (!this.allLinkedFields) {
			this.$('.dataset-id').addClass('is-not-displayed')
			return
		}

		// #11847 Don't allow changing field within Manual Filter
		if (this.lockField) {
			this.$('[name=datasetId]').prop('disabled', true)
		}

		const select = this.$('[name=datasetId]')
		let datasetId = this.getDatasetId()

		let options = _.values(this.model?.datasetsById)
		if (this.model?.id === datasetId && this.model?.attributes?.rootDatasetId !== datasetId) {
			// #31753 This is a view, so we don't want to include it in the dataset
			// dropdown
			options = _.filter(options, o => o.id !== datasetId)
			datasetId = this.model?.attributes?.rootDatasetId
		}

		// #50865 only show datasets in the dropdown, and not dataset views
		options = _.filter(options, o => o.get('type') === DATASET)

		selects.filtering(select, {
			autofocus: true,
			options: _.map(options, 'attributes'),
			accessors: {
				value: 'id'
			}
		})
		selects.val(select, datasetId)

		this.datasetMarkedField = new MarkedField({
			el: select,
			validators: [requiredValidator]
		}).render()

		this.changeDataset()
	},

	renderDatasetButtonControls: function() {
		this.$('.dataset-button').toggleClass('is-not-displayed', !this.showDatasetButton)
		if (!this.showDatasetButton) {
			return
		}

		let datasetButtonControls = new DatasetButtonControls({
			dataset: this.model
		})
		this.$('.dataset-button').append(datasetButtonControls.el)
		datasetButtonControls.render()

		datasetButtonControls.on('change', dataset => {
			this.model = wrapModel(dataset, Dataset)
			this.render()
		})
	},

	getDatasetId: function() {
		const fieldId = _.get(this.options.filter, 'fieldId')
		// #57962 fetch the field only from the native dataset, not a linked
		// dataset
		let datasetId = _.get(this.model?.fields.get(fieldId),
			'datasetId', this.model?.id)
		const vlfId = _.get(this.options.filter, 'visibleLinkedFieldId')
		// #36924 if this filter is based on a VLF, use the dataset it came from for this
		// dataset, rather than the dataset the field originated on, so if we have a chain of links:
		// Dataset A > Dataset B > Dataset C (this one) we will get Dataset B
		if (!_.isNil(vlfId) &&
			// #50860 but ignore the VLF ID if it's negative, which indicates it
			// was a fake VLF ID generated by MissingVlfCreator
			vlfId > 0) {
			datasetId = this.model?.getDatasetIdByVisibleLinkedFieldId()[vlfId]
		}

		return datasetId
	},

	renderPathToDatasetButton: function() {
		this.pathToDatasetButton = new PathToDatasetButton({
			model: this.model,
			path: _.get(this.options.filter, 'path'),
			datasetIdRetriever: () => this.$('[name=datasetId]').val()
		})
		this.$('.path-to-dataset-button-container').html(this.pathToDatasetButton.el)
		this.pathToDatasetButton.render()
		this.pathToDatasetButton.on('change:dataset', () => {
			this.formView.change()
		})
	},

	/**
	 * Indicates whether we're showing the path to dataset button
	 */
	shouldShowPathToDataset: function() {
		return this.getSelectedDatasetId() != null &&
			this.model?.isAmbiguousPath(this.getSelectedDatasetId())
	},

	togglePathToDatasetButton: function() {
		this.$('.path-to-dataset').toggleClass('is-not-displayed', !this.shouldShowPathToDataset())
	},

	/**
	 * Returns the currently selected datasetId.
	 * @returns {null|number} null if no dataset is selected, or the dataset ID
	 * otherwise
	 */
	getSelectedDatasetId: function() {
		let datasetId = this.$('[name=datasetId]').val()
		return _.isEmpty(datasetId) ? null : parseInt(datasetId)
	},

	renderFieldSelect: function(datasetId) {
		datasetId = datasetId || this.getDatasetId()
		// #11847 Don't allow changing field within Manual Filter
		if (this.lockField) {
			this.$('[name=fieldIdentifier]').prop('disabled', true)
		}

		// render the select
		const disallowed = arrayToHash(_.filter(this.filters, filter =>
			this.filter && this.filter.fieldIdentifier != filter.fieldId))
		const fields = _.filter(this.model?.combinedFields,
			field => !disallowed[field.id])
		let selectedId = this.model?.idsToFieldIdentifier(this.options.filter)
		let autofocus = !this.lockField && !this.allLinkedFields
		let dataset = this.model?.datasetsById[datasetId]
		// #35932 if filter.datasetId is a child dataset id, but we are on the root
		// dataset, we still want to be able to render the filters, so use the rootDatasetId.
		if (!dataset) {
			datasetId = this.model?.get('rootDatasetId')
			dataset = this.model?.datasetsById[datasetId]
		}

		this.fieldField = renderFieldDropdown({
			el: this.$('[name=fieldIdentifier]'),
			dataset,
			allowedFieldIds: _.map(fields, 'id'),
			selectedId,
			selectOptions: { autofocus },
			required: true,
			includeEditFields: this.includeEditFields
		})
	},

	/**
	 * Marks the formView as changed.
	 */
	change: function() {
		this.formView.change()
	},

	changeDataset: function() {
		let datasetId = this.$('[name=datasetId]').val()
		if (!datasetId) {
			this.$('.field-identifier').addClass('is-not-displayed')
		}

		this.togglePathToDatasetButton()

		this.$('.field-identifier').toggleClass('is-not-displayed', !datasetId)
		this.changeField()

		// only reset the path if the dataset is changing
		if (datasetId != this.options.filter.datasetId) {
			this.options.filter.path = null
			this.renderPathToDatasetButton()
		}

		this.togglePathToDatasetButton()
		this.renderFieldSelect(datasetId)
	},

	_getField: function() {
		const fieldId = this.$('[name=fieldIdentifier]').val()
		return this.model?.combinedFieldsById[fieldId]
	},

	// callback for when the field is changed.  updates the operator dropdown
	// and displays any extra controls which are appropriate based on the
	// operator
	changeField: function(e) {
		var fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		// only show the Filter Type dropdown if a field is chosen
		this.$('.filter-type').toggleClass('is-not-displayed', _.isEmpty(fieldIdentifier))
		if (_.isEmpty(fieldIdentifier)) {
			this.$('.filter-options, .choose-filter-toggle, .choose-filter.panel')
				.addClass('is-not-displayed')
			return
		}

		// hide the Add Equation link.  It can be re-enabled later in
		// changeOperator for a sub-view if it makes sense to do so
		this.$('.add-equation').addClass('is-not-displayed')

		this.$('.filter-options').removeClass('is-not-displayed')

		this.$('.choose-filter-toggle').toggleClass('is-not-displayed',
			_.isEmpty(this.filters))

		// populate the operator dropdown
		var field = this.model?.getFieldByFieldIdentifier(fieldIdentifier)
		const underlyingField = this.model?.getUnderlyingField(field)
		var dataType = underlyingField.dataType

		renderStrongOperatorDropdown({
			el: this.$('[name=operator]'),
			parentDomainType: this.options.parentDomainType,
			filter: this.options.filter,
			underlyingField
		})

		// show extra controls for text, date and number fields (if appropriate)

		// #48514 this was originally written to just support the 'rollup'
		// data type, so change geographic data types to 'rollup' for this to
		// work
		let filterOptionDataType = isGeographicOrRollup(dataType) ? ROLLUP :
			dataType
		this.$('.filter-option:not([data-type="'+ filterOptionDataType + '"])').addClass('is-not-displayed')
		this.$('.filter-option[data-type="'+ filterOptionDataType + '"]').removeClass('is-not-displayed')
		_.invokeMap(this.filterViews(), 'cleanup')
		let options = _.extend({}, this.options, {
			model: this.model,
			equations: this.equations,
			el: this.el,
			dataType, // needed for currency & percent
			formView: this.formView,
			allowIncomplete: this.options.allowIncomplete,
			// #18694 needed when working with calculated filters b/c we
			// currently restrict the equation to only refer to the same dataset
			// that this field belongs to
			field
		})

		// If these exist, delete them so their methods don't get invoked later when they shouldn't
		delete this.textFilter
		delete this.rollupFilter
		delete this.numericFilter
		delete this.dateFilter

		if (isNumericDataType(dataType)) {
			// #51132 if changing to or from a percent data type, adjust the
			// filter's values so the user will see the same value that they
			// originally entered
			if (!_.isNil(this.previousDataType)) {
				options.filter =
					this.adjustFilterValuesIfNeeded(dataType, options.filter)
			}

			this.numericFilter = new DatasetsNumericFilter(options).render()
			this.numericFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
		}
		if (dataType === TEXT || dataType === CLUSTER) {
			this.textFilter = new DatasetsTextFilter(options).render()
			this.textFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
		}
		if (dataType === DATE || dataType === DATE_TIME || dataType === TIME ||
			dataType === CALENDAR_PERIOD) {
			this.dateFilter = new DatasetsDateFilterStep1(options).render()
			this.dateFilter.on('edit-equation', this.editEquation.bind(this))
		}
		if (dataType === YES_NO) {
			this.yesNoFilter = new DatasetsYesNoFilter(options).render()
		}

		// must go after DatasetsTextFilter so it processes events after
		// DatasetsTextFilter (e.g. hide the Add Equation link)
		if (isGeographicOrRollup(dataType)) {
			this.rollupFilter = new DatasetsRollupFilterStep1(options).render()
			this.rollupFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
		}
		// update the previous dataType
		this.previousDataType = dataType

		this.changeOperator(e)

		this.renderMatchingToggle(field)
		this.maybeShowZoneAdjustmentBanner()
		this.togglePathToDatasetButton()
	},

	/**
	 * If editng a filter is changing the dataType to or from
	 * a percent datatype, adjust the filter's values so the value(s)
	 * will still be displayed as the user entered them.
	 * @param {String} dataType
	 * @param {Object} filter
	 */
	adjustFilterValuesIfNeeded: function(dataType, filter) {
		if (_.isNil(this.previousDataType)) {
			return filter
		}
		let moveDecimalBy
		if (dataType == PERCENT && this.previousDataType != PERCENT) {
			moveDecimalBy = -2
		}
		if (this.previousDataType == PERCENT && dataType != PERCENT) {
			moveDecimalBy = 2
		}
		if (!_.isNil(moveDecimalBy)) {
			filter.value1 = moveDecimal(filter.value1, moveDecimalBy)
			filter.value2 = moveDecimal(filter.value2, moveDecimalBy)
			let values = []
			_.forEach(filter.values, value => {
				values.push(moveDecimal(value, moveDecimalBy))
			})
			filter.values = values
		}
		return filter
	},

	// callback for when the operator is changed.  toggles the text of the
	// primary button, and updates the wizard progress indicator
	changeOperator: function(e) {
		// only re-render the wizard-progress if changeOperator is called from
		// actually changing the select or if the current number of steps don't
		// equal what should be reflected
		var steps = this.twoSteps() ? 2 : 1
		if ((e && e.type == 'change') || this.options.wizard?.steps != steps) {
			this.options.wizard?.render({
				el: this.el,
				steps: steps
			})
		}

		// note all the views in this.filterViews() separately listen for the
		// change event on [name=operator]

		// If .filter-type doesn't have a sibling after it, it should be full width
		var operator = this.$('[name=operator]').val()
		var textFilterStartsEndsWith = _.includes(beginsEndsWithOperators, operator)
		let hasSiblingSelect = _.includes(['is-any', 'is-not-any', 'is',
			'contains-all',	'contains-any', 'does-not-contain-all',
			'does-not-contain-any', 'in-the-range', 'in-any-period',
			'on-day-of-the-week','in-month-of-the-year', 'on-any-date',
			'at-any-time', 'in-any-period-index', 'is-period'], operator)
		var fullWidth = _.includes(['is-blank', 'not-blank', 'not-in-any-range', 'in-another-tree', 'not-in-another-tree'], operator) ||
			!hasSiblingSelect || textFilterStartsEndsWith ||
			YES_NO == this.dataType() && 'equals-equation' == operator ||
			isGeographicOrRollup(this.dataType()) && !_.includes(['contains-all','contains-any', 'does-not-contain-all', 'does-not-contain-any'], operator)
		fullWidth = fullWidth || isNumericDataType(this.dataType()) && !hasSiblingSelect
		this.$('.filter-type').toggleClass('is-full-width', fullWidth)
			.toggleClass('begins-ends-with-filter', textFilterStartsEndsWith)

		// if this is needed by text or numeric filters, they will show it
		this.$('[name=values], .select.control.is-any').addClass('is-not-displayed')
		_.invoke(this.numericFilter, '_applyOperator')
		_.invoke(this.textFilter, 'changeOperator')
		_.invoke(this.rollupFilter, 'changeOperator')
		_.invoke(this.yesNoFilter, 'changeOperator')
		_.invoke(this.dateFilter, 'changeOperator')
		this.formView && this.formView.clearErrors()

		this.maybeShowZoneAdjustmentBanner()
	},

	/**
	 * Handles changes to an operator whose values are rendered in the
	 * [name=values] select
	 * @param {String[]} operators the operators the operators grouped together such that
	 * switching between these operators doesn't clear out the values select
	 */
	changeValuesOperator: function(...operators) {
		var operator = this.$('[name=operator]').val()
		if (_.includes(operators, operator)) {
			this.$('.add-equation').toggleClass('is-not-displayed', !_.isNil(this.rollupFilter))

			let { values, equations } = this.options.filter
			values = values || []
			// #42615 filter currency values that round to the same thing
			const field = this.model?.combinedFieldsById[this.options.filter?.fieldId]
			if (field && isNumericDataType(field.dataType)) {
				values = filterNumericValues(values, field)
			}

			equations = equations || []
			values = values.concat(equations)
			// remember previous selections when switching between these operators
			if (_.includes(operators, this.oldOperator)) {
				values = this.$('[name=values]').val()
			} else {
				this.equations = _.clone(equations)
				this.allEquations = _.clone(equations)
			}
			this.renderValuesSelect(values)
		}
	},


	/**
	 * Callback for when a value is modified in the [name=values] dropdown
	 */
	onChangeEquationSelect: function() {
		this.formView && this.formView.clearErrors()
		let values = this.equationSelect.val()
		// if an equation is no longer chosen as one of the values, remove it
		// from this.equations, but leave it in this.allEquations
		_.remove(this.equations, equation => {
			return !_.includes(values, equation)
		})
		// if a value was previously noted to be an equation and was then
		// removed and re-added, make sure it's put back into the equations
		// array so we know to treat it as an equation and not a value on the
		// server
		_.each(this.allEquations, equation => {
			if (_.includes(values, equation) && !_.includes(this.equations, equation)) {
				this.equations.push(equation)
			}
		})
	},

	renderValuesSelect: function(values) {
		var operator = this.$('[name=operator]').val()

		// suggestions just for this field
		let fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		const field = this.model?.getFieldByFieldIdentifier(fieldIdentifier) || {}
		let suggestions = this.getSuggestionsForFieldId(field.id)
		// #42615 round currency to the nearest dollar
		if (isNumericDataType(field.dataType)) {
			suggestions = filterNumericValues(suggestions, field)
		}
		renderValuesSelect({
			dataset: this.model,
			fieldId: field.id,
			el: this.$('[name=values]'),
			equations: this.equations,
			operator,
			values,
			dataType: field.dataType,
			precision: field.precision,
			currencyCode: field.currencyCode,
			suggestions,
			sorter: new Sorter({
				alphanumeric: !isNumericDataType(field.dataType),
				caseSensitive: false
			}),
			valueConverter: this._convertValue.bind(this)
		})

		this.bindSelectItems()
	},

	/**
	 * Passed to render-values-select for the 'valueConverter'
	 * This ensures that newly-entered values for fields with dataType PERCENT
	 * have a "create new" item label that reflects what was entered
	 * (ex.  typing in 33 should display as "+ Add 33%" instead of "+ Add 3300%")
	 * #51132
	 **/
	_convertValue(value) {
		let fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		let field = this.model?.getFieldByFieldIdentifier(fieldIdentifier) ?? {}
		let dataType = field.dataType
		return convertPercentValuesForValuesSelect(dataType, value)
},

	getSuggestionsForFieldId(fieldId) {
		const allSuggestions = this.options.wizard.xhr.responseJSON
		return allSuggestions[fieldId]
	},

	/*
	 * Bind an event handler all the rich select items so we can handle click events ourselves
	 */
	bindSelectItems: function () {
		let self = this
		d3.select(selects.el(this.$('[name=values]')).get(0))
			.selectAll('.item')
			.on('click.datasets-text-filter', function (e, d) {
				if (d.dataset.type === 'equation') {
					self.editEquation(d.value)
					return false
				}
			}, true)
	},

	/**
	 * Adds or edits an equation in the select designated by this.selectSelector
	 */
	editEquation: function(oldEquation) {
		if (!_.isString(oldEquation)) {
			oldEquation = undefined
		}
		var fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		let form = new DatasetsFilterEquationForm({
			model: this.model,
			dataType: this.equationDataType(),
			title: i18n.get('scorecards.node.metric.equation.set'),
			equation: oldEquation,
			field: this.field(),
			visibleLinkedField: this.model?.getVisibleLinkedFieldByFieldIdentifier(fieldIdentifier)
		})
		appDisplay.dialog(form)
		form.on('done', (equation) => {
			this.formView.change()
			this.formView.clearErrors()
			this.onEquationChanged(oldEquation, equation)
		})
	},

	onEquationChanged: function(oldEquation, equation) {
		if (!_.isNil(oldEquation) && _.isString(oldEquation)) {
			_.remove(this.equations, eq => eq == oldEquation)
			_.remove(this.allEquations, eq => eq == oldEquation)
		}
		this.equations.push(equation)
		this.allEquations.push(equation)

		if (_.includes([TEXT, CLUSTER], this.dataType()) ||
			isNumericDataType(this.dataType())) {
			let el = this.equationSelect
			// #17133 for some reason, cleanup of child views of filter-step1 isn't
			// happening properly in the dashboards section so let's make sure the
			// [name=values] multi-select is there to verify we are really working
			// with a text filter
			if (!_.isEmpty(el)) {
				let values = el.val() || []
				let index = _.findIndex(values, value => value == oldEquation)
				if (index == -1) {
					values.push(equation)
				} else {
					values.splice(index, 1, equation)
				}
				this.renderValuesSelect(values)
			}
			if (isNumericDataType(this.dataType())) {
				this.numericFilter.onEquationChanged(oldEquation, equation, this.equations)
			}
		} else if (_.includes([DATE, TIME, DATE_TIME], this.dataType())) {
			this.dateFilter.onEquationChanged(oldEquation, equation)
		}
	},

	/**
	 * Returns the field currently being edited (or null if one hasn't been
	 * chosen yet)
	 */
	field: function() {
		var fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		if (!fieldIdentifier) {
			return null
		}

		const field = this.model?.getFieldByFieldIdentifier(fieldIdentifier)
		return this.model?.getUnderlyingField(field)
	},

	/**
	 * Returns the data type of the field for which we are currently building
	 * a filter (or null if a field hasn't been chosen yet)
	 */
	dataType: function() {
		return _.get(this.field(), 'dataType', null)
	},

	/**
	 * The data type expected to be produced by the function
	 */
	equationDataType: function() {
		const operator = this.$('[name=operator]').val()
		switch (operator) {
			case 'on-any-date': return DATE
			case 'at-any-time': return TIME
			default: return this.dataType()
		}
	},

	/**
	 * Returns the selector which identifies the select that may contain
	 * equations
	 */
	equationSelector: function() {
		return `[name=${this.nonEquationField()}]`
	},

	nonEquationField: function() {
		return _.includes([DATE, DATE_TIME], this.dataType()) ? 'dates'
			: 'values'
	},

	// returns whether there are two steps in the dialog (if there aren't two
	// steps there's just one step).  If the dataType and operator aren't
	// specified, their values will be read from the DOM
	twoSteps: function(dataType, operator) {
		dataType = dataType || this.dataType()
		operator = operator || this.$('[name=operator]').val()

		return twoSteps(dataType, operator)
	},

	clickBadge: function(e) {
		this.formView.change()
		this.$('.badge').removeClass('is-selected')
		this.$(e.currentTarget).addClass('is-selected')
	},

	test: function() {
		var problem = FormView.prototype.test.apply(this, arguments)

		var dataType = this.dataType()
		if (_.includes([TEXT, DATE], dataType)) {
			problem = problem || _.invoke(this.textFilter, 'test')
		}

		if (isDateTimeDataType(dataType) || dataType === CALENDAR_PERIOD) {
			problem = problem || this.dateFilter?.test?.()
		}

		if (isNumericDataType(dataType)) {
			problem = problem || this.numericFilter?.test?.()
		}

		if (isGeographicOrRollup(dataType)) {
			problem = problem || this.rollupFilter?.test?.()
		}

		if (dataType === YES_NO) {
			problem = problem || this.yesNoFilter?.test?.()
		}

		if (!this.$('.choose-filter.panel').hasClass('is-not-displayed')) {
			if (_.isEmpty(this.$('.badge.is-selected'))) {
				return i18n.get('datasets.orGroup.required')
			}
		}

		if (this.shouldShowPathToDataset() &&
			_.isEmpty(this.pathToDatasetButton.path)) {
			return i18n.get('datasets.linked.choosePathToDataset.required')
		}

		return problem
	},

	saveState: function() {
		this.options.model = this.model // #57573

		var filter = this.options.filter
		_.extend(filter, formHelper.readFromDom(this.el))
		_.extend(filter, this.model?.fieldIdentifierToIds(filter.fieldIdentifier))
		_.invokeMap(this.filterViews(), 'saveState')
		const field = unwrapModel(this.model?.getFieldByIds(filter))

		// #42615 if the filter is on a field with a numeric data type, put all the suggestions
		// that were rounded up or down into filter.values
		if (isNumericDataType(field.dataType)) {
			const suggestions = this.getSuggestionsForFieldId(field.id)
			_.forEach(filter.values, value => {
				const matchedValuesFromSuggestions =
					getAllValuesFromSuggestionsForFilterValue(value, suggestions, field)
				// if the user picked a suggestion from the list, or typed a suggested
				// value exactly, remove the original value
				if (matchedValuesFromSuggestions.length > 0) {
					filter.values = _.filter(filter.values, filterVal =>
						filterVal !== value)
				}
				filter.values = _.concat(filter.values, matchedValuesFromSuggestions)
			})
			filter.values = _(filter.values)
				.map(value => {
					const valueAsFloat = parseFloat(value)
					return isNaN(valueAsFloat) ? value : valueAsFloat
				})
				.uniq()
				.value()
		}

		// set the filter's calendar id if this is a filter on a calendar period field
		if (field.dataType === CALENDAR_PERIOD) {
			// #46390  only set calendarId to the default if we don't have one yet
			// #46559 and this is not an existing filter (either saved to the db
			// or previously completed - indicated by having the property 'valid'
			// populated)
			let editingExisting = _.has(filter, 'id') || _.has(filter, 'valid')
			if (_.isNil(filter.calendarId) && !editingExisting) {
				filter.calendarId = field.defaultCalendarId
			}
		}

		if (this.model?.isAmbiguousPath(this.getSelectedDatasetId())) {
			filter.path = this.pathToDatasetButton.path
		} else {
			delete filter.path
		}
		delete filter.fieldIdentifier

		// if the user selected "Add to Existing Filter As An Or"
		if (this.chooseFilterToggle.val()) {
			var badge = this.$('.badge.is-selected')
			// all the filters in the badge have the same group so that we can
			// just look at the first one
			var filterEl = badge.find('.filter')
			var index = _.findIndex(this.$('.filter-badges .filter'), (el) => filterEl[0] == el)
			filter.linkGroup = this.filterBadges.filteredFilters[index].linkGroup
		} else {
			if (this.inLinkGroup || _.isUndefined(filter.linkGroup)) {
				// move this out into its own or group
				filter.linkGroup = nextLinkGroup(this.filters)
			}
		}

		// #17112 don't hold onto equations for yes/no filters unless the
		// "equals equation" operator was chosen
		if (field.dataType === YES_NO && filter.operator !== 'equals-equation') {
			delete filter.equations
		}

		// handle the filter's "equations" property
		// if the non-equation field has values
		let nonEquationField = this.nonEquationField()
		if (!isEmpty(filter[nonEquationField])) {
			// pull any equations out of it
			if (!_.isEmpty(this.equations)) {
				_.pull(filter[nonEquationField], ...this.equations)
			}
			// #45308 don't erase the equations for begins with and ends with
			if (!beginsEndsWithOperators.includes(filter.operator)) {
				// #26810 Make the filter has the updated equations
				filter.equations = this.equations
			}
			// if the non-equation field has just one value, wrap it in an array
			if (!_.isArray(filter[nonEquationField])) {
				filter[nonEquationField] = [filter[nonEquationField]]
			}
		}

		// Sometimes when toggling between operator types, we end up with an
		// array with an empty string it it. This is bad; trim it out
		if (filter.values) {
			filter.values = trimArray(filter.values)
		}

		if (filter.equations) {
			filter.equations = trimArray(filter.equations)
		}

		if (_.includes([DATE, DATE_TIME, TIME], field.dataType) &&
				// #35990 We don't have enough information to cleanup two-step filter
				// properties at this stage. That will be handled later by the
				// specific filter views/pickers
				!this.twoSteps()) {
			deleteNonApplicableFilterProperties(filter)
		}
	},

	submit: function() {
		this.saveState()
		if (this.twoSteps()) {
			this.options.wizard.next()
		} else {
			this.options.wizard.finish()
			this.trigger('done')
		}
	},

	deleteFilter: function () {
		this.options.wizard.trigger('delete')
		this.trigger('done')
	},

	// returns all the filter sub-views
	filterViews: function() {
		return [this.numericFilter, this.textFilter, this.dateFilter,
			this.rollupFilter, this.yesNoFilter]
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.wizardProgress, this.formView, this.fieldField,
			this.operatorField, this.chooseFilterToggle, ...this.filterViews(),
			this.datasetMarkedField, this.matchingToggle], 'remove')
	}

})

export default FilterStep1

FilterStep1.__filename__ = 'datasets/filters/filter-step1.js'
